import { AppErrorCard } from '@components/error-card/app-error-card';
import { ScreenReaderElement } from '@components/screen-reader/screen-reader-element';
import { StandardButton } from '@eika/button';
import { IconTypes } from '@eika/icon';
import { Link } from '@eika/link';
import type { RequestException } from '@eika-infrastruktur/request';
import { useLinks } from '@hooks/use-links';
import { useAccountForChildProcessRules } from '@react-query/hooks/query/use-account-for-child-process-rules';
import { format } from '@translation-utils/use-translation';
import { useTranslation } from '@translations';
import Skeleton from 'react-loading-skeleton';
import './scholarship-details.scss';
import { InfoMessage } from '@eika/attention-message';
import { formatNok } from '@utils/text-utils';

export const ScholarshipDetails = () => {
    const { scholarshipDetails, tip } = useTranslation();
    const { lanekassenWealthRules } = useLinks();
    const { data: childProcessRules, isPending, error, refetch } = useAccountForChildProcessRules();

    if (isPending) {
        return <ScholarshipDetailsSkeleton />;
    }

    if (error) {
        return <ScholarshipDetailsError error={error} onRetry={() => refetch()} />;
    }

    const { scholarshipAmountThreshold } = childProcessRules;

    return (
        <div className="scholarship-details" data-testid="scholarship-details">
            <div>
                <h2 className="typography-subtitle-bold">{scholarshipDetails.header}</h2>
                <p>
                    {format(
                        scholarshipDetails.ingress,
                        formatNok(scholarshipAmountThreshold.value),
                        scholarshipAmountThreshold.year,
                    )}
                </p>
                <Link
                    href={lanekassenWealthRules.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    icon={IconTypes.EKSTERN_24}
                    iconOnRightSide
                >
                    {scholarshipDetails.linkText}
                </Link>
            </div>
            <InfoMessage header={tip} text={scholarshipDetails.tip} icon={IconTypes.LYSPAERE_24} />
        </div>
    );
};

const ScholarshipDetailsSkeleton = () => (
    <div className="scholarship-details" data-testid="scholarship-details-skeleton">
        <Skeleton height={32} width="50%" />
        <Skeleton count={2} />
    </div>
);

interface ScholarshipDetailsErrorProps {
    error: RequestException;
    onRetry: () => void;
}

const ScholarshipDetailsError = ({ error, onRetry }: ScholarshipDetailsErrorProps) => {
    const { scholarshipDetails, tryAgain } = useTranslation();

    return (
        <AppErrorCard description={scholarshipDetails.error} exception={error} data-testid="scholarship-details-error">
            <ScreenReaderElement text={scholarshipDetails.error} role="alert" />
            <StandardButton onClick={onRetry}>{tryAgain}</StandardButton>
        </AppErrorCard>
    );
};
